import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Tag, Gavel, MapPin, X, Upload, Trash2, Package, 
  Sparkles, Wand2, Loader2, Car, Bike, Heart, HandHeart, RefreshCw, Video, Play, Info
} from "lucide-react";
import api from "@/utils/api";
import { processMultipleImages, ACCEPTED_IMAGE_FORMATS } from "@/utils/imageUtils";

// Accepted video formats
const ACCEPTED_VIDEO_FORMATS = ".mp4,.webm,.mov";
const MAX_VIDEO_SIZE_MB = 100; // 100MB max for videos

// Vehicle type options
const VEHICLE_TYPES = [
  { value: "car_truck", label: "Car/Truck" },
  { value: "motorcycle", label: "Motorcycle" },
  { value: "powersport", label: "Powersport (ATV/UTV)" },
  { value: "rv_camper", label: "RV/Camper" },
  { value: "trailer", label: "Trailer" },
  { value: "boat", label: "Boat" },
  { value: "commercial", label: "Commercial/Industrial" },
  { value: "other", label: "Other" }
];

// Generate years from current year to 1900
const VEHICLE_YEARS = Array.from({ length: new Date().getFullYear() - 1899 }, (_, i) => (new Date().getFullYear() + 1) - i);

// Common vehicle makes
const VEHICLE_MAKES = [
  "Acura", "Alfa Romeo", "Aston Martin", "Audi", "Bentley", "BMW", "Buick", "Cadillac",
  "Chevrolet", "Chrysler", "Dodge", "Ferrari", "Fiat", "Ford", "Genesis", "GMC",
  "Honda", "Hyundai", "Infiniti", "Jaguar", "Jeep", "Kia", "Lamborghini", "Land Rover",
  "Lexus", "Lincoln", "Lotus", "Maserati", "Mazda", "McLaren", "Mercedes-Benz", "Mini",
  "Mitsubishi", "Nissan", "Polestar", "Porsche", "Ram", "Rivian", "Rolls-Royce", "Subaru",
  "Suzuki", "Tesla", "Toyota", "Volkswagen", "Volvo", "Other"
];

// Motorcycle makes
const MOTORCYCLE_MAKES = [
  "Aprilia", "BMW", "Ducati", "Harley-Davidson", "Honda", "Indian", "Kawasaki",
  "KTM", "Moto Guzzi", "MV Agusta", "Royal Enfield", "Suzuki", "Triumph", "Yamaha", "Other"
];

// Body styles for cars
const CAR_BODY_STYLES = [
  "Sedan", "Coupe", "Convertible", "Hatchback", "SUV", "Crossover", 
  "Truck", "Van", "Minivan", "Wagon", "Sports Car", "Luxury", "Other"
];

// Motorcycle body styles
const MOTORCYCLE_BODY_STYLES = [
  "Cruiser", "Sport", "Touring", "Standard", "Dual Sport", "Dirt Bike",
  "Scooter", "Trike", "Chopper", "Bobber", "Cafe Racer", "Adventure", "Other"
];

// Transmission types
const TRANSMISSIONS = [
  { value: "automatic", label: "Automatic" },
  { value: "manual", label: "Manual" },
  { value: "cvt", label: "CVT" },
  { value: "semi_automatic", label: "Semi-Automatic" },
  { value: "other", label: "Other" }
];

// Fuel types
const FUEL_TYPES = [
  { value: "gasoline", label: "Gasoline" },
  { value: "diesel", label: "Diesel" },
  { value: "electric", label: "Electric" },
  { value: "hybrid", label: "Hybrid" },
  { value: "plug_in_hybrid", label: "Plug-in Hybrid" },
  { value: "flex_fuel", label: "Flex Fuel" },
  { value: "other", label: "Other" }
];

// Drivetrain options
const DRIVETRAINS = [
  { value: "fwd", label: "Front-Wheel Drive (FWD)" },
  { value: "rwd", label: "Rear-Wheel Drive (RWD)" },
  { value: "awd", label: "All-Wheel Drive (AWD)" },
  { value: "4wd", label: "4-Wheel Drive (4WD)" },
  { value: "other", label: "Other" }
];

// Title status options
const TITLE_STATUS = [
  { value: "clean", label: "Clean Title" },
  { value: "salvage", label: "Salvage Title" },
  { value: "rebuilt", label: "Rebuilt Title" },
  { value: "flood", label: "Flood Title" },
  { value: "lemon", label: "Lemon Title" },
  { value: "bonded", label: "Bonded Title" },
  { value: "parts_only", label: "Parts Only (No Title)" },
  { value: "other", label: "Other" }
];

// Auction duration options
const AUCTION_DURATIONS = [
  { value: "1", label: "1 Day" },
  { value: "3", label: "3 Days" },
  { value: "5", label: "5 Days" },
  { value: "7", label: "7 Days" },
  { value: "10", label: "10 Days" },
  { value: "14", label: "14 Days" },
  { value: "30", label: "30 Days" }
];

// Product Size Options by Category
const SIZE_OPTIONS = {
  clothing: [
    { value: "xxs", label: "XXS" },
    { value: "xs", label: "XS" },
    { value: "s", label: "S" },
    { value: "m", label: "M" },
    { value: "l", label: "L" },
    { value: "xl", label: "XL" },
    { value: "xxl", label: "XXL" },
    { value: "3xl", label: "3XL" },
    { value: "4xl", label: "4XL" },
    { value: "one_size", label: "One Size" }
  ],
  shoes: [
    { value: "5", label: "US 5" },
    { value: "5.5", label: "US 5.5" },
    { value: "6", label: "US 6" },
    { value: "6.5", label: "US 6.5" },
    { value: "7", label: "US 7" },
    { value: "7.5", label: "US 7.5" },
    { value: "8", label: "US 8" },
    { value: "8.5", label: "US 8.5" },
    { value: "9", label: "US 9" },
    { value: "9.5", label: "US 9.5" },
    { value: "10", label: "US 10" },
    { value: "10.5", label: "US 10.5" },
    { value: "11", label: "US 11" },
    { value: "11.5", label: "US 11.5" },
    { value: "12", label: "US 12" },
    { value: "13", label: "US 13" },
    { value: "14", label: "US 14" }
  ],
  jewelry: [
    { value: "5", label: "Ring Size 5" },
    { value: "6", label: "Ring Size 6" },
    { value: "7", label: "Ring Size 7" },
    { value: "8", label: "Ring Size 8" },
    { value: "9", label: "Ring Size 9" },
    { value: "10", label: "Ring Size 10" },
    { value: "one_size", label: "One Size" },
    { value: "adjustable", label: "Adjustable" }
  ],
  general: [
    { value: "small", label: "Small" },
    { value: "medium", label: "Medium" },
    { value: "large", label: "Large" },
    { value: "extra_large", label: "Extra Large" },
    { value: "one_size", label: "One Size" },
    { value: "n_a", label: "N/A" }
  ]
};

// Color Options
const COLOR_OPTIONS = [
  { value: "black", label: "Black", hex: "#000000" },
  { value: "white", label: "White", hex: "#FFFFFF" },
  { value: "gray", label: "Gray", hex: "#808080" },
  { value: "silver", label: "Silver", hex: "#C0C0C0" },
  { value: "red", label: "Red", hex: "#FF0000" },
  { value: "blue", label: "Blue", hex: "#0000FF" },
  { value: "navy", label: "Navy", hex: "#000080" },
  { value: "green", label: "Green", hex: "#008000" },
  { value: "yellow", label: "Yellow", hex: "#FFFF00" },
  { value: "orange", label: "Orange", hex: "#FFA500" },
  { value: "purple", label: "Purple", hex: "#800080" },
  { value: "pink", label: "Pink", hex: "#FFC0CB" },
  { value: "brown", label: "Brown", hex: "#8B4513" },
  { value: "tan", label: "Tan", hex: "#D2B48C" },
  { value: "beige", label: "Beige", hex: "#F5F5DC" },
  { value: "cream", label: "Cream", hex: "#FFFDD0" },
  { value: "gold", label: "Gold", hex: "#FFD700" },
  { value: "rose_gold", label: "Rose Gold", hex: "#B76E79" },
  { value: "multicolor", label: "Multicolor", hex: "linear-gradient(90deg, red, orange, yellow, green, blue, purple)" }
];

// Common vehicle features
const VEHICLE_FEATURES = [
  "Air Conditioning", "Bluetooth", "Backup Camera", "Navigation System",
  "Leather Seats", "Heated Seats", "Sunroof/Moonroof", "Remote Start",
  "Cruise Control", "Adaptive Cruise Control", "Lane Departure Warning",
  "Blind Spot Monitor", "Apple CarPlay", "Android Auto", "Premium Sound System",
  "Third Row Seating", "Tow Package", "Alloy Wheels", "Power Windows",
  "Power Locks", "Keyless Entry", "Push Button Start"
];

// Charity organizations
const CHARITIES = [
  { id: "red_cross", name: "American Red Cross", category: "Disaster Relief", logo: "🏥", description: "Provides emergency assistance and disaster relief" },
  { id: "st_jude", name: "St. Jude Children's Research Hospital", category: "Healthcare", logo: "👶", description: "Leading children's hospital for cancer and other diseases" },
  { id: "feeding_america", name: "Feeding America", category: "Hunger Relief", logo: "🍽️", description: "Nation's largest domestic hunger-relief organization" },
  { id: "habitat", name: "Habitat for Humanity", category: "Housing", logo: "🏠", description: "Builds homes for families in need" },
  { id: "unicef", name: "UNICEF USA", category: "Children", logo: "🌍", description: "Works to save children's lives worldwide" },
  { id: "wounded_warrior", name: "Wounded Warrior Project", category: "Veterans", logo: "🎖️", description: "Supports injured veterans and their families" },
  { id: "salvation_army", name: "The Salvation Army", category: "Community", logo: "❤️", description: "Provides social services and disaster relief" },
  { id: "aspca", name: "ASPCA", category: "Animals", logo: "🐾", description: "Prevents cruelty to animals across the country" },
  { id: "make_a_wish", name: "Make-A-Wish Foundation", category: "Children", logo: "⭐", description: "Grants wishes to children with critical illnesses" },
  { id: "doctors_without_borders", name: "Doctors Without Borders", category: "Healthcare", logo: "🩺", description: "Provides medical care in crisis situations" },
  { id: "world_wildlife", name: "World Wildlife Fund", category: "Environment", logo: "🐼", description: "Protects wildlife and natural habitats" },
  { id: "goodwill", name: "Goodwill Industries", category: "Employment", logo: "💼", description: "Provides job training and employment services" },
  { id: "boys_girls_clubs", name: "Boys & Girls Clubs of America", category: "Youth", logo: "🧒", description: "Provides after-school programs for young people" },
  { id: "special_olympics", name: "Special Olympics", category: "Sports", logo: "🏅", description: "Empowers people with intellectual disabilities through sports" },
  { id: "local_food_bank", name: "Local Food Bank", category: "Hunger Relief", logo: "🥫", description: "Support your local community food bank" }
];

// Donation percentage options
const DONATION_PERCENTAGES = [
  { value: 5, label: "5%" },
  { value: 10, label: "10%" },
  { value: 15, label: "15%" },
  { value: 25, label: "25%" },
  { value: 50, label: "50%" },
  { value: 100, label: "100% (Full donation)" }
];

const SellPage = ({ user }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    listing_type: "fixed",
    price: "",
    reserve_price: "",
    // Product Variations
    item_type: "single", // "single" or "multi"
    quantity: 1,
    size: "",
    colors: [], // Can select multiple colors
    brand: "",
    // Shopify-style variants (overrides simple colors/quantity if used)
    variants: [], // [{tempId, color, size, price, quantity, image}]
    condition: "good",
    images: [],
    video: null, // Video URL
    location: null,
    seller_paypal_email: "",
    shipping: {
      free_shipping: false,
      local_rate: "",
      national_rate: "",
      international_rate: "",
      handling_time: "1-3 business days"
    },
    package_weight_oz: "",
    package_length: "",
    package_width: "",
    package_height: "",
    local_pickup_available: false,
    local_pickup_address: "",
    // Subcategory field
    subcategory: "",
    // Vehicle-specific fields
    vehicle_type: "",
    vehicle_year: "",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_trim: "",
    vehicle_vin: "",
    vehicle_mileage: "",
    vehicle_transmission: "",
    vehicle_fuel_type: "",
    vehicle_drivetrain: "",
    vehicle_body_style: "",
    vehicle_exterior_color: "",
    vehicle_interior_color: "",
    vehicle_engine: "",
    vehicle_title_status: "",
    vehicle_features: [],
    // Charity donation fields
    charity_enabled: false,
    charity_id: "",
    charity_percentage: 10,
    // Auction duration (in days)
    auction_duration: "7",
    // Auto-relist settings
    auto_relist: false,
    auto_relist_count: 3
  });
  
  // Check if selected category is vehicle-related
  const isVehicleCategory = ['vehicles'].includes(formData.category) || 
    (formData.category === 'vehicles' && ['cars', 'motorcycles', 'parts'].includes(formData.subcategory));
  
  // Get selected charity details
  const selectedCharity = CHARITIES.find(c => c.id === formData.charity_id);
  
  // Get subcategories for selected category
  const selectedCategory = categories.find(c => c.id === formData.category);
  const subcategories = selectedCategory?.subcategories || [];
  
  const [submitting, setSubmitting] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [hasTaxInfo, setHasTaxInfo] = useState(null);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [generatingTitle, setGeneratingTitle] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [freeListingsStatus, setFreeListingsStatus] = useState(null);

  useEffect(() => {
    if (!user) {
      toast.error("Please login to create a listing");
      navigate('/');
      return;
    }
    
    api.get("/seller/tax-info/check").then(res => {
      setHasTaxInfo(res.data.has_tax_info);
    }).catch(() => {
      setHasTaxInfo(false);
    });
    
    // Fetch free listings status
    api.get("/seller/free-listings-status").then(res => {
      setFreeListingsStatus(res.data);
    }).catch(() => {});
    
    api.get("/categories").then(res => setCategories(res.data));
  }, [user, navigate]);

  const [locationInput, setLocationInput] = useState('');
  const [lookingUpLocation, setLookingUpLocation] = useState(false);

  const lookupLocation = async () => {
    if (!locationInput.trim()) {
      toast.error("Please enter a zip code or city name");
      return;
    }

    setLookingUpLocation(true);
    try {
      // Check if input looks like a US zip code (5 digits)
      const isUSZipCode = /^\d{5}(-\d{4})?$/.test(locationInput.trim());
      
      // Build the query - add "USA" for zip codes to avoid international confusion
      let searchQuery = locationInput.trim();
      if (isUSZipCode) {
        searchQuery = `${locationInput.trim()}, USA`;
      }
      
      // Use Nominatim API to geocode the location
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&limit=1&addressdetails=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const result = data[0];
        setFormData(prev => ({
          ...prev,
          location: {
            latitude: parseFloat(result.lat),
            longitude: parseFloat(result.lon),
            city: result.address?.city || result.address?.town || result.address?.village || result.address?.county || "",
            state: result.address?.state || "",
            country: result.address?.country || "",
            country_code: result.address?.country_code?.toUpperCase() || "",
            zip_code: result.address?.postcode || ""
          }
        }));
        toast.success("Location found!");
        setLocationInput('');
      } else {
        toast.error("Location not found. Try a different format (e.g., '90210' or 'Los Angeles, CA')");
      }
    } catch (error) {
      toast.error("Failed to lookup location. Please try again.");
    } finally {
      setLookingUpLocation(false);
    }
  };

  const detectLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${position.coords.latitude}&lon=${position.coords.longitude}&format=json`
          );
          const data = await response.json();
          
          setFormData(prev => ({
            ...prev,
            location: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              city: data.address?.city || data.address?.town || data.address?.village || "",
              state: data.address?.state || "",
              country: data.address?.country || "",
              country_code: data.address?.country_code?.toUpperCase() || "",
              zip_code: data.address?.postcode || ""
            }
          }));
          toast.success("Location detected!");
        } catch (error) {
          setFormData(prev => ({
            ...prev,
            location: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              city: "",
              state: "",
              country: "",
              country_code: "",
              zip_code: ""
            }
          }));
          toast.success("Location coordinates saved");
        }
        setDetectingLocation(false);
      },
      (error) => {
        toast.error("Failed to detect location. Please allow location access.");
        setDetectingLocation(false);
      }
    );
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const remainingSlots = 20 - formData.images.length;
    if (remainingSlots <= 0) {
      toast.error("Maximum 20 images allowed");
      return;
    }
    
    setUploadingImages(true);
    try {
      const { images, errors } = await processMultipleImages(
        files.slice(0, remainingSlots),
        remainingSlots,
        (status) => toast.info(status, { duration: 1000 })
      );
      
      if (images.length > 0) {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, ...images].slice(0, 20)
        }));
        toast.success(`${images.length} image(s) added`);
      }
      
      if (errors.length > 0) {
        errors.forEach(err => {
          toast.error(`Failed to process ${err.file}: ${err.error}`);
        });
      }
    } catch (error) {
      toast.error("Failed to process images");
    } finally {
      setUploadingImages(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a valid video file (MP4, WebM, or MOV)");
      return;
    }

    // Validate file size
    const maxSizeBytes = MAX_VIDEO_SIZE_MB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      toast.error(`Video must be under ${MAX_VIDEO_SIZE_MB}MB`);
      return;
    }

    setUploadingVideo(true);
    setVideoUploadProgress(0);

    try {
      // Create FormData for upload
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      // Upload with progress tracking
      const response = await api.post('/upload', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setVideoUploadProgress(percentCompleted);
        },
      });

      if (response.data.url) {
        setFormData(prev => ({
          ...prev,
          video: response.data.url
        }));
        toast.success("Video uploaded successfully!");
      } else {
        throw new Error("No URL returned from upload");
      }
    } catch (error) {
      console.error("Video upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload video");
    } finally {
      setUploadingVideo(false);
      setVideoUploadProgress(0);
      e.target.value = '';
    }
  };

  const removeVideo = () => {
    setFormData(prev => ({
      ...prev,
      video: null
    }));
  };

  const generateAIDescription = async () => {
    if (!formData.title) {
      toast.error("Please enter a title first");
      return;
    }
    setGeneratingDescription(true);
    try {
      const res = await api.post('/ai/generate-description', {
        title: formData.title,
        category: formData.category,
        condition: formData.condition
      });
      setFormData(prev => ({ ...prev, description: res.data.description }));
      toast.success("AI description generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate description");
    } finally {
      setGeneratingDescription(false);
    }
  };

  const improveTitleWithAI = async () => {
    if (!formData.title) {
      toast.error("Please enter a title first");
      return;
    }
    setGeneratingTitle(true);
    try {
      const res = await api.post('/ai/improve-title', {
        title: formData.title,
        category: formData.category
      });
      if (res.data.suggestions && res.data.suggestions.length > 0) {
        const suggestions = res.data.suggestions;
        toast.success(
          <div className="space-y-2">
            <p className="font-medium">AI Title Suggestions:</p>
            {suggestions.map((s, i) => (
              <button
                key={i}
                onClick={() => {
                  setFormData(prev => ({ ...prev, title: s }));
                  toast.dismiss();
                  toast.success("Title updated!");
                }}
                className="block w-full text-left p-2 bg-white/10 rounded hover:bg-white/20 text-sm"
              >
                {s}
              </button>
            ))}
          </div>,
          { duration: 10000 }
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate suggestions");
    } finally {
      setGeneratingTitle(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.description || !formData.category || !formData.price) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (formData.images.length === 0) {
      toast.error("Please add at least one image for your listing");
      return;
    }
    
    // Validate vehicle-specific required fields
    if (isVehicleCategory) {
      if (!formData.vehicle_year || !formData.vehicle_make || !formData.vehicle_model) {
        toast.error("Please fill in vehicle year, make, and model");
        return;
      }
      if (!formData.vehicle_mileage) {
        toast.error("Please enter vehicle mileage");
        return;
      }
      if (!formData.vehicle_title_status) {
        toast.error("Please select vehicle title status");
        return;
      }
    }

    setSubmitting(true);
    try {
      // Convert variants to API format
      const apiVariants = (formData.variants || [])
        .filter(v => (v.color && v.color.trim()) || (v.size && v.size.trim()) || (v.quantity !== '' && v.quantity !== null))
        .map(v => ({
          variant_id: v.variant_id || `var_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          color: v.color?.trim() || null,
          size: v.size?.trim() || null,
          price: v.price !== '' && v.price !== null && v.price !== undefined ? parseFloat(v.price) : null,
          quantity: parseInt(v.quantity || 0, 10) || 0,
          image: (v.image_index !== null && v.image_index !== undefined && v.image_index >= 0)
            ? formData.images[v.image_index] : null
        }));
      const listingData = {
        ...formData,
        price: parseFloat(formData.price),
        quantity: parseInt(formData.quantity) || 1,
        variants: apiVariants.length > 0 ? apiVariants : undefined,
        reserve_price: formData.reserve_price ? parseFloat(formData.reserve_price) : null,
        item_weight: formData.item_weight ? parseFloat(formData.item_weight) : null,
        shipping: {
          free_shipping: formData.shipping.free_shipping,
          local_rate: formData.shipping.local_rate ? parseFloat(formData.shipping.local_rate) : null,
          national_rate: formData.shipping.national_rate ? parseFloat(formData.shipping.national_rate) : null,
          international_rate: formData.shipping.international_rate ? parseFloat(formData.shipping.international_rate) : null,
          handling_time: formData.shipping.handling_time
        },
        // Include vehicle data if applicable
        vehicle_details: isVehicleCategory ? {
          vehicle_type: formData.vehicle_type,
          year: formData.vehicle_year,
          make: formData.vehicle_make,
          model: formData.vehicle_model,
          trim: formData.vehicle_trim,
          vin: formData.vehicle_vin,
          mileage: formData.vehicle_mileage ? parseInt(formData.vehicle_mileage) : null,
          transmission: formData.vehicle_transmission,
          fuel_type: formData.vehicle_fuel_type,
          drivetrain: formData.vehicle_drivetrain,
          body_style: formData.vehicle_body_style,
          exterior_color: formData.vehicle_exterior_color,
          interior_color: formData.vehicle_interior_color,
          engine: formData.vehicle_engine,
          title_status: formData.vehicle_title_status,
          features: formData.vehicle_features
        } : null,
        // Include charity donation data if enabled
        charity_donation: formData.charity_enabled && formData.charity_id ? {
          charity_id: formData.charity_id,
          charity_name: selectedCharity?.name || "",
          percentage: formData.charity_percentage,
          estimated_amount: ((parseFloat(formData.price) || 0) * formData.charity_percentage / 100).toFixed(2)
        } : null
      };
      const response = await api.post("/listings", listingData);
      toast.success("Listing created successfully!");
      navigate(`/item/${response.data.listing_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create listing");
    } finally {
      setSubmitting(false);
    }
  };
  
  // Auto-generate title when vehicle details change
  const generateVehicleTitle = () => {
    if (isVehicleCategory && formData.vehicle_year && formData.vehicle_make && formData.vehicle_model) {
      const titleParts = [formData.vehicle_year, formData.vehicle_make, formData.vehicle_model];
      if (formData.vehicle_trim) titleParts.push(formData.vehicle_trim);
      return titleParts.join(' ');
    }
    return '';
  };

  return (
    <div className="min-h-screen pt-52 pb-16 bg-slate-50" data-testid="sell-page">
      <div className="container mx-auto px-4 md:px-6 max-w-2xl">
        <div className="text-center mb-8">
          <h1 className="font-outfit text-3xl font-bold mb-2">Create a Listing</h1>
          <p className="text-muted-foreground">List your item for sale or auction</p>
          <Button 
            variant="link" 
            onClick={() => navigate('/sell/bulk')} 
            className="text-emerald-600 mt-2"
            data-testid="bulk-listing-link"
          >
            Have multiple items? Try Bulk Listing →
          </Button>
          
          {/* Import from eBay Link */}
          <div className="mt-3 pt-3 border-t border-slate-200">
            <Button 
              variant="link" 
              onClick={() => navigate('/import/ebay')} 
              className="text-blue-600 flex items-center gap-2"
              data-testid="ebay-import-link"
            >
              <svg viewBox="0 0 60 24" className="h-4 w-auto">
                <text x="0" y="18" fill="#E53238" fontWeight="bold" fontSize="16">e</text>
                <text x="10" y="18" fill="#0064D2" fontWeight="bold" fontSize="16">b</text>
                <text x="22" y="18" fill="#F5AF02" fontWeight="bold" fontSize="16">a</text>
                <text x="34" y="18" fill="#86B817" fontWeight="bold" fontSize="16">y</text>
              </svg>
              Already selling on eBay? Import your listings →
            </Button>
          </div>
        </div>

        {/* Free Listings Banner */}
        {freeListingsStatus && (
          <div className={`mb-6 p-4 rounded-xl border ${
            freeListingsStatus.launch_mode || freeListingsStatus.is_in_free_tier 
              ? 'bg-emerald-50 border-emerald-200' 
              : 'bg-slate-50 border-slate-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  freeListingsStatus.launch_mode || freeListingsStatus.is_in_free_tier ? 'bg-emerald-100' : 'bg-slate-200'
                }`}>
                  <Tag className={`h-5 w-5 ${
                    freeListingsStatus.launch_mode || freeListingsStatus.is_in_free_tier ? 'text-emerald-600' : 'text-slate-500'
                  }`} />
                </div>
                <div>
                  {freeListingsStatus.launch_mode ? (
                    <>
                      <p className="font-semibold text-emerald-800">
                        🎉 0% Seller Fees - Launch Special!
                      </p>
                      <p className="text-sm text-emerald-600">
                        {freeListingsStatus.message || "Sell with zero fees during our launch period"}
                      </p>
                    </>
                  ) : freeListingsStatus.is_in_free_tier ? (
                    <>
                      <p className="font-semibold text-emerald-800">
                        🎉 {freeListingsStatus.free_listings_remaining} Free Sales Remaining!
                      </p>
                      <p className="text-sm text-emerald-600">
                        Sell up to {freeListingsStatus.free_listings_limit} items with zero fees
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold text-slate-700">
                        Standard Seller Fees Apply
                      </p>
                      <p className="text-sm text-slate-500">
                        {freeListingsStatus.current_fee_percent}% fee on sales (+ applicable taxes)
                      </p>
                    </>
                  )}
                </div>
              </div>
              {!freeListingsStatus.launch_mode && (
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Total Sales</p>
                  <p className="text-lg font-bold">{freeListingsStatus.total_sales}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Verification Info Banner */}
        <div className="mb-6 p-4 rounded-xl border bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-100 flex-shrink-0">
              <Info className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-blue-800">
                List items now, verify later
              </p>
              <p className="text-sm text-blue-600">
                You can list items and sell without ID verification. Identity verification is only required when you request a payout.
              </p>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`step-indicator ${step >= s ? (step > s ? 'completed' : 'active') : 'pending'}`}>
                {s}
              </div>
              {s < 3 && <div className={`w-12 h-0.5 ${step > s ? 'bg-emerald-500' : 'bg-muted'}`} />}
            </div>
          ))}
        </div>

        <Card className="p-6">
          {step === 1 && (
            <div className="space-y-6" data-testid="step-1">
              <div>
                <Label className="mb-2 block">Listing Type</Label>
                <div className="listing-type-toggle">
                  <button 
                    className={formData.listing_type === 'fixed' ? 'active' : ''}
                    onClick={() => setFormData({ ...formData, listing_type: 'fixed' })}
                    data-testid="fixed-price-btn"
                  >
                    <Tag className="h-4 w-4 inline mr-2" /> Fixed Price
                  </button>
                  <button 
                    className={formData.listing_type === 'auction' ? 'active' : ''}
                    onClick={() => setFormData({ ...formData, listing_type: 'auction' })}
                    data-testid="auction-btn"
                  >
                    <Gavel className="h-4 w-4 inline mr-2" /> Auction
                  </button>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Title *</Label>
                  <div className="flex gap-2">
                    {isVehicleCategory && formData.vehicle_year && formData.vehicle_make && formData.vehicle_model && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-blue-600 hover:text-blue-700 gap-1"
                        onClick={() => setFormData({ ...formData, title: generateVehicleTitle() })}
                        data-testid="use-vehicle-title-btn"
                      >
                        <Car className="h-3 w-3" />
                        Use Vehicle Title
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-emerald-600 hover:text-emerald-700 gap-1"
                      onClick={improveTitleWithAI}
                      disabled={generatingTitle || !formData.title}
                      data-testid="ai-improve-title-btn"
                    >
                      {generatingTitle ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Wand2 className="h-3 w-3" />
                      )}
                      AI Suggest
                    </Button>
                  </div>
                </div>
                <Input 
                  placeholder={isVehicleCategory ? "e.g., 2023 Honda Civic Sport" : "What are you selling?"}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  data-testid="title-input"
                />
                {isVehicleCategory && !formData.title && formData.vehicle_year && formData.vehicle_make && formData.vehicle_model && (
                  <p className="text-xs text-blue-600 mt-1">
                    💡 Tip: Click "Use Vehicle Title" to auto-fill: {generateVehicleTitle()}
                  </p>
                )}
              </div>

              <div>
                <Label className="mb-2 block">Category *</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({ ...formData, category: v, subcategory: "" })}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Subcategory Selection - Shows when category has subcategories */}
              {subcategories.length > 0 && (
                <div>
                  <Label className="mb-2 block">Subcategory</Label>
                  <Select 
                    value={formData.subcategory || "all"} 
                    onValueChange={(v) => setFormData({ ...formData, subcategory: v === "all" ? "" : v })}
                  >
                    <SelectTrigger data-testid="subcategory-select">
                      <SelectValue placeholder="Select a subcategory (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All {selectedCategory?.name}</SelectItem>
                      {subcategories.map((subcat) => (
                        <SelectItem key={subcat.id} value={subcat.id}>{subcat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Vehicle Details Section - Shows when vehicle category is selected */}
              {isVehicleCategory && (
                <div className="space-y-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Car className="h-5 w-5 text-blue-600" />
                    <h3 className="font-semibold text-blue-900">Vehicle Details</h3>
                  </div>

                  {/* Vehicle Type */}
                  <div>
                    <Label className="mb-2 block">Vehicle Type *</Label>
                    <Select 
                      value={formData.vehicle_type} 
                      onValueChange={(v) => setFormData({ ...formData, vehicle_type: v })}
                    >
                      <SelectTrigger data-testid="vehicle-type-select">
                        <SelectValue placeholder="Select vehicle type" />
                      </SelectTrigger>
                      <SelectContent>
                        {VEHICLE_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Year, Make, Model Row */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="mb-2 block">Year *</Label>
                      <Select 
                        value={formData.vehicle_year} 
                        onValueChange={(v) => setFormData({ ...formData, vehicle_year: v })}
                      >
                        <SelectTrigger data-testid="vehicle-year-select">
                          <SelectValue placeholder="Year" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {VEHICLE_YEARS.map((year) => (
                            <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="mb-2 block">Make *</Label>
                      <Select 
                        value={formData.vehicle_make} 
                        onValueChange={(v) => setFormData({ ...formData, vehicle_make: v })}
                      >
                        <SelectTrigger data-testid="vehicle-make-select">
                          <SelectValue placeholder="Make" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {(formData.vehicle_type === 'motorcycle' ? MOTORCYCLE_MAKES : VEHICLE_MAKES).map((make) => (
                            <SelectItem key={make} value={make}>{make}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="mb-2 block">Model *</Label>
                      <Input 
                        placeholder="e.g., Civic, F-150"
                        value={formData.vehicle_model}
                        onChange={(e) => setFormData({ ...formData, vehicle_model: e.target.value })}
                        data-testid="vehicle-model-input"
                      />
                    </div>
                  </div>

                  {/* Trim & Body Style Row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="mb-2 block">Trim Level</Label>
                      <Input 
                        placeholder="e.g., EX, Sport, Limited"
                        value={formData.vehicle_trim}
                        onChange={(e) => setFormData({ ...formData, vehicle_trim: e.target.value })}
                        data-testid="vehicle-trim-input"
                      />
                    </div>
                    <div>
                      <Label className="mb-2 block">Body Style</Label>
                      <Select 
                        value={formData.vehicle_body_style} 
                        onValueChange={(v) => setFormData({ ...formData, vehicle_body_style: v })}
                      >
                        <SelectTrigger data-testid="vehicle-body-style-select">
                          <SelectValue placeholder="Select body style" />
                        </SelectTrigger>
                        <SelectContent>
                          {(formData.vehicle_type === 'motorcycle' ? MOTORCYCLE_BODY_STYLES : CAR_BODY_STYLES).map((style) => (
                            <SelectItem key={style} value={style}>{style}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* VIN Number */}
                  <div>
                    <Label className="mb-2 block">VIN (Vehicle Identification Number)</Label>
                    <Input 
                      placeholder="17-character VIN"
                      value={formData.vehicle_vin}
                      onChange={(e) => setFormData({ ...formData, vehicle_vin: e.target.value.toUpperCase() })}
                      maxLength={17}
                      data-testid="vehicle-vin-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Enter VIN to help verify vehicle history and build buyer confidence
                    </p>
                  </div>

                  {/* Mileage & Title Status Row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="mb-2 block">Mileage *</Label>
                      <Input 
                        type="number"
                        placeholder="e.g., 45000"
                        value={formData.vehicle_mileage}
                        onChange={(e) => setFormData({ ...formData, vehicle_mileage: e.target.value })}
                        data-testid="vehicle-mileage-input"
                      />
                    </div>
                    <div>
                      <Label className="mb-2 block">Title Status *</Label>
                      <Select 
                        value={formData.vehicle_title_status} 
                        onValueChange={(v) => setFormData({ ...formData, vehicle_title_status: v })}
                      >
                        <SelectTrigger data-testid="vehicle-title-status-select">
                          <SelectValue placeholder="Select title status" />
                        </SelectTrigger>
                        <SelectContent>
                          {TITLE_STATUS.map((status) => (
                            <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Transmission, Fuel Type, Drivetrain Row */}
                  {formData.vehicle_type !== 'trailer' && formData.vehicle_type !== 'boat' && (
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="mb-2 block">Transmission</Label>
                        <Select 
                          value={formData.vehicle_transmission} 
                          onValueChange={(v) => setFormData({ ...formData, vehicle_transmission: v })}
                        >
                          <SelectTrigger data-testid="vehicle-transmission-select">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {TRANSMISSIONS.map((trans) => (
                              <SelectItem key={trans.value} value={trans.value}>{trans.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="mb-2 block">Fuel Type</Label>
                        <Select 
                          value={formData.vehicle_fuel_type} 
                          onValueChange={(v) => setFormData({ ...formData, vehicle_fuel_type: v })}
                        >
                          <SelectTrigger data-testid="vehicle-fuel-type-select">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {FUEL_TYPES.map((fuel) => (
                              <SelectItem key={fuel.value} value={fuel.value}>{fuel.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="mb-2 block">Drivetrain</Label>
                        <Select 
                          value={formData.vehicle_drivetrain} 
                          onValueChange={(v) => setFormData({ ...formData, vehicle_drivetrain: v })}
                        >
                          <SelectTrigger data-testid="vehicle-drivetrain-select">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {DRIVETRAINS.map((dt) => (
                              <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {/* Colors Row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="mb-2 block">Exterior Color</Label>
                      <Input 
                        placeholder="e.g., Silver, Black, Red"
                        value={formData.vehicle_exterior_color}
                        onChange={(e) => setFormData({ ...formData, vehicle_exterior_color: e.target.value })}
                        data-testid="vehicle-exterior-color-input"
                      />
                    </div>
                    <div>
                      <Label className="mb-2 block">Interior Color</Label>
                      <Input 
                        placeholder="e.g., Black Leather, Tan Cloth"
                        value={formData.vehicle_interior_color}
                        onChange={(e) => setFormData({ ...formData, vehicle_interior_color: e.target.value })}
                        data-testid="vehicle-interior-color-input"
                      />
                    </div>
                  </div>

                  {/* Engine */}
                  <div>
                    <Label className="mb-2 block">Engine</Label>
                    <Input 
                      placeholder="e.g., 2.0L 4-Cylinder Turbo, V8 5.0L"
                      value={formData.vehicle_engine}
                      onChange={(e) => setFormData({ ...formData, vehicle_engine: e.target.value })}
                      data-testid="vehicle-engine-input"
                    />
                  </div>

                  {/* Vehicle Features */}
                  <div>
                    <Label className="mb-2 block">Features & Options</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-2 bg-white rounded-lg border">
                      {VEHICLE_FEATURES.map((feature) => (
                        <label key={feature} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={formData.vehicle_features.includes(feature)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({ ...formData, vehicle_features: [...formData.vehicle_features, feature] });
                              } else {
                                setFormData({ ...formData, vehicle_features: formData.vehicle_features.filter(f => f !== feature) });
                              }
                            }}
                            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                          {feature}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Description *</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-emerald-600 hover:text-emerald-700 gap-1"
                    onClick={generateAIDescription}
                    disabled={generatingDescription || !formData.title}
                    data-testid="ai-generate-description-btn"
                  >
                    {generatingDescription ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Sparkles className="h-3 w-3" />
                    )}
                    AI Write
                  </Button>
                </div>
                <Textarea 
                  placeholder="Describe your item in detail... or click 'AI Write' to generate!"
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="description-input"
                />
              </div>

              <Button 
                className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600"
                onClick={() => setStep(2)}
                disabled={!formData.title || !formData.category || !formData.description}
                data-testid="next-step-1"
              >
                Continue
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6" data-testid="step-2">
              <div>
                <Label className="mb-2 block">Photos (up to 20)</Label>
                <div className="image-upload-zone" onClick={() => !uploadingImages && document.getElementById('image-input').click()}>
                  {uploadingImages ? (
                    <>
                      <Loader2 className="h-8 w-8 mx-auto text-emerald-500 mb-2 animate-spin" />
                      <p className="text-sm text-emerald-600">Processing images...</p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">Click to upload images</p>
                      <p className="text-xs text-muted-foreground mt-1">Supports JPG, PNG, HEIC</p>
                    </>
                  )}
                  <input 
                    id="image-input"
                    type="file"
                    accept={ACCEPTED_IMAGE_FORMATS}
                    multiple
                    className="hidden"
                    onChange={handleImageUpload}
                    disabled={uploadingImages}
                    data-testid="image-upload"
                  />
                </div>
                {formData.images.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 mt-4">
                    {formData.images.map((img, i) => (
                      <div key={i} className="relative aspect-square rounded-lg overflow-hidden group">
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        <button 
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                          onClick={() => removeImage(i)}
                        >
                          <Trash2 className="h-5 w-5 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Video Upload Section */}
              <div>
                <Label className="mb-2 block flex items-center gap-2">
                  <Video className="h-4 w-4" />
                  Video (Optional)
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Add a video to showcase your item. Max {MAX_VIDEO_SIZE_MB}MB. Supports MP4, WebM, MOV.
                </p>
                
                {!formData.video ? (
                  <div 
                    className="video-upload-zone border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/50 transition-colors"
                    onClick={() => !uploadingVideo && document.getElementById('video-input').click()}
                  >
                    {uploadingVideo ? (
                      <div className="flex flex-col items-center">
                        <Loader2 className="h-8 w-8 animate-spin text-emerald-500 mb-2" />
                        <p className="text-sm text-muted-foreground">Uploading video... {videoUploadProgress}%</p>
                        <div className="w-full max-w-xs mt-2 bg-slate-200 rounded-full h-2">
                          <div 
                            className="bg-emerald-500 h-2 rounded-full transition-all duration-300" 
                            style={{ width: `${videoUploadProgress}%` }}
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <Video className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">Click to upload a video</p>
                        <p className="text-xs text-muted-foreground mt-1">MP4, WebM, or MOV up to {MAX_VIDEO_SIZE_MB}MB</p>
                      </>
                    )}
                    <input 
                      id="video-input"
                      type="file"
                      accept={ACCEPTED_VIDEO_FORMATS}
                      className="hidden"
                      onChange={handleVideoUpload}
                      disabled={uploadingVideo}
                      data-testid="video-upload"
                    />
                  </div>
                ) : (
                  <div className="relative rounded-lg overflow-hidden border border-slate-200">
                    <video 
                      src={formData.video} 
                      controls 
                      className="w-full max-h-64 object-contain bg-black"
                    />
                    <button 
                      className="absolute top-2 right-2 p-2 bg-red-500 hover:bg-red-600 rounded-full text-white transition-colors"
                      onClick={removeVideo}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                      <Play className="h-3 w-3" />
                      Video attached
                    </div>
                  </div>
                )}
              </div>

              {/* Product Variations Section */}
              <div className="space-y-6 p-4 bg-slate-50 rounded-xl border">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Package className="h-5 w-5 text-emerald-600" />
                  Product Details
                </h3>

                {/* Quantity - Single vs Multi Item */}
                <div>
                  <Label className="mb-2 block font-medium">QUANTITY</Label>
                  <p className="text-sm text-muted-foreground mb-3">Select a single or multi item.</p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={formData.item_type === "single" ? "default" : "outline"}
                      className={formData.item_type === "single" ? "bg-slate-800 hover:bg-slate-700" : ""}
                      onClick={() => setFormData({ ...formData, item_type: "single", quantity: 1 })}
                      data-testid="single-item-btn"
                    >
                      Single Item
                    </Button>
                    <Button
                      type="button"
                      variant={formData.item_type === "multi" ? "default" : "outline"}
                      className={formData.item_type === "multi" ? "bg-slate-800 hover:bg-slate-700" : ""}
                      onClick={() => setFormData({ ...formData, item_type: "multi" })}
                      data-testid="multi-item-btn"
                    >
                      Multi Item
                    </Button>
                  </div>
                  {formData.item_type === "multi" && (
                    <div className="mt-3 flex items-center gap-3">
                      <Input 
                        type="number"
                        placeholder="Quantity"
                        className="w-24"
                        value={formData.quantity}
                        onChange={(e) => setFormData({ ...formData, quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                        min="1"
                        data-testid="quantity-input"
                      />
                      <span className="text-sm text-muted-foreground">items available</span>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    {formData.item_type === "single" 
                      ? "You're selling one item."
                      : "If you have more than one of the same item for sale, select Multi Item to specify the quantity."}
                  </p>
                </div>

                {/* Size Selection */}
                <div className="border-t pt-4">
                  <Label className="mb-2 block font-medium">SIZE</Label>
                  <Select 
                    value={formData.size} 
                    onValueChange={(v) => setFormData({ ...formData, size: v })}
                  >
                    <SelectTrigger data-testid="size-select">
                      <SelectValue placeholder="Select Size" />
                    </SelectTrigger>
                    <SelectContent>
                      {(SIZE_OPTIONS[formData.category] || SIZE_OPTIONS.general).map((size) => (
                        <SelectItem key={size.value} value={size.value}>
                          {size.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Brand Input */}
                <div className="border-t pt-4">
                  <Label className="mb-2 block font-medium">BRAND</Label>
                  <p className="text-sm text-muted-foreground mb-2">Optional - Add details to help your buyers find your item quickly.</p>
                  <Input 
                    placeholder="Enter the Brand/Designer"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    data-testid="brand-input"
                  />
                </div>

                {/* Color Selection */}
                <div className="border-t pt-4">
                  <Label className="mb-2 block font-medium">COLOR</Label>
                  <p className="text-sm text-muted-foreground mb-3">Optional - Select all colors that apply.</p>
                  <div className="flex flex-wrap gap-2">
                    {COLOR_OPTIONS.map((color) => {
                      const isSelected = formData.colors.includes(color.value);
                      const canSelect = true; // Allow unlimited color selection
                      return (
                        <button
                          key={color.value}
                          type="button"
                          disabled={!canSelect && !isSelected}
                          onClick={() => {
                            if (isSelected) {
                              setFormData({ ...formData, colors: formData.colors.filter(c => c !== color.value) });
                            } else if (canSelect) {
                              setFormData({ ...formData, colors: [...formData.colors, color.value] });
                            }
                          }}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-all ${
                            isSelected 
                              ? "border-emerald-500 bg-emerald-50 text-emerald-700" 
                              : canSelect 
                                ? "border-slate-200 hover:border-slate-300" 
                                : "opacity-50 cursor-not-allowed"
                          }`}
                          data-testid={`color-${color.value}`}
                        >
                          <span 
                            className="w-4 h-4 rounded-full border border-slate-300"
                            style={{ 
                              background: color.hex.includes('gradient') ? color.hex : color.hex,
                              borderColor: color.value === 'white' ? '#e2e8f0' : 'transparent'
                            }}
                          />
                          {color.label}
                          {isSelected && <span className="text-emerald-600">✓</span>}
                        </button>
                      );
                    })}
                  </div>
                  {formData.colors.length > 0 && (
                    <p className="text-xs text-emerald-600 mt-2">
                      Selected: {formData.colors.map(c => COLOR_OPTIONS.find(co => co.value === c)?.label).join(", ")}
                    </p>
                  )}
                </div>
              </div>

              {/* Variants Section (Shopify-style) */}
              <div>
                <Label className="mb-2 block font-medium">VARIANTS · STOCK PER OPTION</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Optional. Add variants if this item comes in multiple colors / sizes with their own stock counts.
                  Leave empty if you only have one type.
                </p>
                <div className="space-y-2">
                  {formData.variants.map((v, idx) => (
                    <div key={v.tempId || idx} className="grid grid-cols-12 gap-2 items-center p-2 border rounded-lg bg-slate-50">
                      <Input
                        className="col-span-3"
                        placeholder="Color (e.g. Red)"
                        value={v.color || ''}
                        onChange={(e) => {
                          const next = [...formData.variants];
                          next[idx] = { ...next[idx], color: e.target.value };
                          setFormData({ ...formData, variants: next });
                        }}
                        data-testid={`variant-color-${idx}`}
                      />
                      <Input
                        className="col-span-2"
                        placeholder="Size (S, M, 10)"
                        value={v.size || ''}
                        onChange={(e) => {
                          const next = [...formData.variants];
                          next[idx] = { ...next[idx], size: e.target.value };
                          setFormData({ ...formData, variants: next });
                        }}
                        data-testid={`variant-size-${idx}`}
                      />
                      <Input
                        className="col-span-2"
                        type="number"
                        min="0"
                        placeholder="Qty"
                        value={v.quantity ?? ''}
                        onChange={(e) => {
                          const next = [...formData.variants];
                          next[idx] = { ...next[idx], quantity: e.target.value };
                          setFormData({ ...formData, variants: next });
                        }}
                        data-testid={`variant-qty-${idx}`}
                      />
                      <Input
                        className="col-span-2"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Price (opt)"
                        value={v.price ?? ''}
                        onChange={(e) => {
                          const next = [...formData.variants];
                          next[idx] = { ...next[idx], price: e.target.value };
                          setFormData({ ...formData, variants: next });
                        }}
                        data-testid={`variant-price-${idx}`}
                      />
                      <Select
                        value={(v.image_index ?? '').toString()}
                        onValueChange={(val) => {
                          const next = [...formData.variants];
                          next[idx] = { ...next[idx], image_index: val === '' ? null : parseInt(val, 10) };
                          setFormData({ ...formData, variants: next });
                        }}
                      >
                        <SelectTrigger className="col-span-2" data-testid={`variant-img-${idx}`}>
                          <SelectValue placeholder="Photo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="-1">No photo</SelectItem>
                          {formData.images.map((_, i) => (
                            <SelectItem key={i} value={i.toString()}>Image {i + 1}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="col-span-1 text-red-500 hover:text-red-600"
                        onClick={() => {
                          const next = formData.variants.filter((_, i) => i !== idx);
                          setFormData({ ...formData, variants: next });
                        }}
                        data-testid={`variant-remove-${idx}`}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        variants: [
                          ...formData.variants,
                          { tempId: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, color: '', size: '', quantity: '', price: '', image_index: null }
                        ]
                      });
                    }}
                    data-testid="add-variant-btn"
                  >
                    + Add a variant
                  </Button>
                </div>
              </div>

              {/* Condition Section */}
              <div>
                <Label className="mb-2 block font-medium">CONDITION</Label>
                <p className="text-sm text-muted-foreground mb-2">Specify the condition of your item.</p>
                <Select 
                  value={formData.condition} 
                  onValueChange={(v) => setFormData({ ...formData, condition: v })}
                >
                  <SelectTrigger data-testid="condition-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New - Brand new, never used</SelectItem>
                    <SelectItem value="like_new">Like New - Opened but never used</SelectItem>
                    <SelectItem value="excellent">Excellent - Minimal signs of use</SelectItem>
                    <SelectItem value="good">Good - Normal wear, fully functional</SelectItem>
                    <SelectItem value="fair">Fair - Visible wear, works as intended</SelectItem>
                    <SelectItem value="used">Used - Shows wear from regular use</SelectItem>
                    <SelectItem value="pre_owned">Pre-Owned - Previously owned</SelectItem>
                    <SelectItem value="refurbished">Refurbished - Restored to working condition</SelectItem>
                    <SelectItem value="for_parts">For Parts/Not Working</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Location Section */}
              <div>
                <Label className="mb-2 block">Location (for map view)</Label>
                {formData.location ? (
                  <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-emerald-600" />
                        <div>
                          <p className="font-medium text-emerald-900">
                            {[
                              formData.location.city,
                              formData.location.state,
                              formData.location.zip_code
                            ].filter(Boolean).join(', ') || 
                            formData.location.country || 
                            "Location set"}
                          </p>
                          <p className="text-xs text-emerald-700">
                            {formData.location.country}
                          </p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setFormData({ ...formData, location: null })}
                        type="button"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Zip Code / City Input */}
                    <div className="flex gap-2">
                      <Input 
                        placeholder="Enter zip code or city, state (e.g., 90210 or Los Angeles, CA)"
                        value={locationInput}
                        onChange={(e) => setLocationInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), lookupLocation())}
                        className="flex-1"
                        data-testid="location-input"
                      />
                      <Button 
                        variant="default"
                        className="bg-emerald-500 hover:bg-emerald-600"
                        onClick={lookupLocation}
                        disabled={lookingUpLocation}
                        type="button"
                      >
                        {lookingUpLocation ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Lookup"
                        )}
                      </Button>
                    </div>
                    
                    {/* Or use GPS */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-px bg-slate-200" />
                      <span className="text-xs text-muted-foreground">or</span>
                      <div className="flex-1 h-px bg-slate-200" />
                    </div>
                    
                    <Button 
                      variant="outline" 
                      className="w-full gap-2"
                      onClick={detectLocation}
                      disabled={detectingLocation}
                      type="button"
                      data-testid="detect-location-btn"
                    >
                      <MapPin className="h-4 w-4" />
                      {detectingLocation ? "Detecting..." : "Use My Current Location"}
                    </Button>
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  Adding a location helps buyers find your item on the global map
                </p>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1 rounded-full" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button 
                  className="flex-1 rounded-full bg-emerald-500 hover:bg-emerald-600"
                  onClick={() => setStep(3)}
                  data-testid="next-step-2"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6" data-testid="step-3">
              <div>
                <Label className="mb-2 block">
                  {formData.listing_type === 'auction' ? 'Starting Bid *' : 'Price *'}
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input 
                    type="number"
                    placeholder="0.00"
                    className="pl-7"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    min="0.01"
                    step="0.01"
                    data-testid="price-input"
                  />
                </div>
              </div>

              {/* Reserve Price for Auctions */}
              {formData.listing_type === 'auction' && (
                <div>
                  <Label className="mb-2 block">Reserve Price (Optional)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input 
                      type="number"
                      placeholder="Minimum price to sell"
                      className="pl-7"
                      value={formData.reserve_price}
                      onChange={(e) => setFormData({ ...formData, reserve_price: e.target.value })}
                      min="0"
                      step="0.01"
                      data-testid="reserve-price-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    The auction will only complete if bidding reaches this amount. Buyers won&apos;t see the exact reserve.
                  </p>
                </div>
              )}

              {formData.listing_type === 'auction' && (
                <div>
                  <Label className="mb-2 block">Auction Duration *</Label>
                  <Select
                    value={formData.auction_duration}
                    onValueChange={(value) => setFormData({ ...formData, auction_duration: value })}
                  >
                    <SelectTrigger data-testid="auction-duration-select">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      {AUCTION_DURATIONS.map((duration) => (
                        <SelectItem key={duration.value} value={duration.value}>
                          {duration.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    How long your auction will run. Longer durations may attract more bidders.
                  </p>
                </div>
              )}

              {/* Auto-Relist Option */}
              <div className="border rounded-lg p-4 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="h-5 w-5 text-blue-500" />
                    <h3 className="font-semibold">Auto-Relist</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, auto_relist: !formData.auto_relist })}
                    className={`w-12 h-6 rounded-full transition ${formData.auto_relist ? 'bg-blue-500' : 'bg-gray-300'}`}
                    data-testid="auto-relist-toggle"
                  >
                    <span className={`block w-5 h-5 bg-white rounded-full shadow transform transition ${formData.auto_relist ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Automatically relist your item if it doesn't sell or the auction ends without meeting the reserve.
                </p>
                {formData.auto_relist && (
                  <div className="mt-3">
                    <Label className="mb-1 block text-sm">Number of times to relist</Label>
                    <Select
                      value={String(formData.auto_relist_count)}
                      onValueChange={(value) => setFormData({ ...formData, auto_relist_count: parseInt(value) })}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 time</SelectItem>
                        <SelectItem value="2">2 times</SelectItem>
                        <SelectItem value="3">3 times</SelectItem>
                        <SelectItem value="5">5 times</SelectItem>
                        <SelectItem value="0">Unlimited</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Charity Donation Section */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Heart className="h-5 w-5 text-pink-500" />
                    Donate to Charity
                  </h3>
                  <button
                    type="button"
                    onClick={() => setFormData({
                      ...formData,
                      charity_enabled: !formData.charity_enabled,
                      charity_id: formData.charity_enabled ? "" : formData.charity_id
                    })}
                    className={`w-12 h-6 rounded-full transition ${formData.charity_enabled ? 'bg-pink-500' : 'bg-gray-300'}`}
                    data-testid="charity-toggle"
                  >
                    <span className={`block w-5 h-5 bg-white rounded-full shadow transform transition ${formData.charity_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  Make a difference! Donate a portion of your sale to help those in need.
                </p>

                {formData.charity_enabled && (
                  <div className="space-y-4 p-4 bg-pink-50 rounded-xl border border-pink-200">
                    {/* Donation Percentage */}
                    <div>
                      <Label className="mb-2 block">Donation Amount</Label>
                      <div className="flex flex-wrap gap-2">
                        {DONATION_PERCENTAGES.map((pct) => (
                          <button
                            key={pct.value}
                            type="button"
                            onClick={() => setFormData({ ...formData, charity_percentage: pct.value })}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                              formData.charity_percentage === pct.value
                                ? 'bg-pink-500 text-white'
                                : 'bg-white border border-pink-200 text-pink-700 hover:bg-pink-100'
                            }`}
                            data-testid={`charity-pct-${pct.value}`}
                          >
                            {pct.label}
                          </button>
                        ))}
                      </div>
                      {formData.price && (
                        <p className="text-sm text-pink-600 mt-2">
                          💝 You&apos;ll donate ${((parseFloat(formData.price) || 0) * formData.charity_percentage / 100).toFixed(2)} to charity
                        </p>
                      )}
                    </div>

                    {/* Charity Selection */}
                    <div>
                      <Label className="mb-2 block">Choose a Charity</Label>
                      <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto pr-2">
                        {CHARITIES.map((charity) => (
                          <button
                            key={charity.id}
                            type="button"
                            onClick={() => setFormData({ ...formData, charity_id: charity.id })}
                            className={`flex items-start gap-3 p-3 rounded-lg text-left transition ${
                              formData.charity_id === charity.id
                                ? 'bg-pink-100 border-2 border-pink-400'
                                : 'bg-white border border-pink-100 hover:border-pink-300'
                            }`}
                            data-testid={`charity-${charity.id}`}
                          >
                            <span className="text-2xl">{charity.logo}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">{charity.name}</span>
                                <span className="text-xs px-2 py-0.5 bg-pink-100 text-pink-600 rounded-full">
                                  {charity.category}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                                {charity.description}
                              </p>
                            </div>
                            {formData.charity_id === charity.id && (
                              <span className="text-pink-500">✓</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Selected Charity Summary */}
                    {selectedCharity && formData.price && (
                      <div className="p-3 bg-white rounded-lg border border-pink-200">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{selectedCharity.logo}</span>
                          <div>
                            <p className="font-medium text-sm">{selectedCharity.name}</p>
                            <p className="text-xs text-pink-600">
                              Will receive ${((parseFloat(formData.price) || 0) * formData.charity_percentage / 100).toFixed(2)} ({formData.charity_percentage}% of sale)
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground">
                      🎗️ Your listing will display a charity badge to attract socially-conscious buyers
                    </p>
                  </div>
                )}
              </div>

              {/* Shipping Options */}
              <div className="border-t pt-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Package className="h-5 w-5 text-emerald-500" />
                  Shipping Options
                </h3>
                
                <div className="space-y-4">
                  {/* Package Dimensions */}
                  <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                    <Label className="text-sm font-medium">Package Weight & Dimensions</Label>
                    <p className="text-xs text-muted-foreground">Enter package details for accurate shipping cost estimates</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs mb-1 block">Weight (oz) *</Label>
                        <Input 
                          type="number"
                          placeholder="16"
                          className="h-9 text-sm"
                          value={formData.package_weight_oz}
                          onChange={(e) => setFormData({ ...formData, package_weight_oz: e.target.value })}
                          min="1"
                          data-testid="package-weight-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {formData.package_weight_oz ? `${(formData.package_weight_oz / 16).toFixed(2)} lbs` : ''}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs mb-1 block">Length (in)</Label>
                        <Input 
                          type="number"
                          placeholder="12"
                          className="h-9 text-sm"
                          value={formData.package_length}
                          onChange={(e) => setFormData({ ...formData, package_length: e.target.value })}
                          min="1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs mb-1 block">Width (in)</Label>
                        <Input 
                          type="number"
                          placeholder="8"
                          className="h-9 text-sm"
                          value={formData.package_width}
                          onChange={(e) => setFormData({ ...formData, package_width: e.target.value })}
                          min="1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs mb-1 block">Height (in)</Label>
                        <Input 
                          type="number"
                          placeholder="4"
                          className="h-9 text-sm"
                          value={formData.package_height}
                          onChange={(e) => setFormData({ ...formData, package_height: e.target.value })}
                          min="1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Free Shipping Toggle */}
                  <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                    <div>
                      <p className="font-medium">Free Shipping</p>
                      <p className="text-xs text-muted-foreground">Offer free shipping to attract more buyers</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData({
                        ...formData,
                        shipping: { ...formData.shipping, free_shipping: !formData.shipping.free_shipping }
                      })}
                      className={`w-12 h-6 rounded-full transition ${formData.shipping.free_shipping ? 'bg-emerald-500' : 'bg-gray-300'}`}
                    >
                      <span className={`block w-5 h-5 bg-white rounded-full shadow transform transition ${formData.shipping.free_shipping ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                  </div>

                  {/* Shipping Rates (if not free) */}
                  {!formData.shipping.free_shipping && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs mb-1 block">Local (Same State)</Label>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                            <Input 
                              type="number"
                              placeholder="5.00"
                              className="pl-6 h-9 text-sm"
                              value={formData.shipping.local_rate}
                              onChange={(e) => setFormData({
                                ...formData,
                                shipping: { ...formData.shipping, local_rate: e.target.value }
                              })}
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs mb-1 block">National (US)</Label>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                            <Input 
                              type="number"
                              placeholder="12.00"
                              className="pl-6 h-9 text-sm"
                              value={formData.shipping.national_rate}
                              onChange={(e) => setFormData({
                                ...formData,
                                shipping: { ...formData.shipping, national_rate: e.target.value }
                              })}
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs mb-1 block">International</Label>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                            <Input 
                              type="number"
                              placeholder="25.00"
                              className="pl-6 h-9 text-sm"
                              value={formData.shipping.international_rate}
                              onChange={(e) => setFormData({
                                ...formData,
                                shipping: { ...formData.shipping, international_rate: e.target.value }
                              })}
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Buyer pays shipping cost. Leave empty if you don&apos;t ship to that region.
                      </p>
                    </div>
                  )}

                  {/* Local Pickup Option */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-blue-500" />
                          Local Pickup Available
                        </p>
                        <p className="text-xs text-muted-foreground">Allow buyers to pick up in person and pay cash</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          local_pickup_available: !formData.local_pickup_available
                        })}
                        className={`w-12 h-6 rounded-full transition ${formData.local_pickup_available ? 'bg-blue-500' : 'bg-gray-300'}`}
                      >
                        <span className={`block w-5 h-5 bg-white rounded-full shadow transform transition ${formData.local_pickup_available ? 'translate-x-6' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                    
                    {formData.local_pickup_available && (
                      <div className="mt-3">
                        <Label className="text-xs mb-1 block">Pickup Location (City/Area)</Label>
                        <Input 
                          placeholder="e.g., Downtown Los Angeles, CA"
                          className="h-9 text-sm"
                          value={formData.local_pickup_address}
                          onChange={(e) => setFormData({ ...formData, local_pickup_address: e.target.value })}
                          data-testid="pickup-location-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Buyers will see this location and can arrange pickup. A unique pickup code will be generated for verification.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Handling Time */}
                  <div>
                    <Label className="text-xs mb-1 block">Handling Time</Label>
                    <Select
                      value={formData.shipping.handling_time}
                      onValueChange={(value) => setFormData({
                        ...formData,
                        shipping: { ...formData.shipping, handling_time: value }
                      })}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Same day">Same day</SelectItem>
                        <SelectItem value="1 business day">1 business day</SelectItem>
                        <SelectItem value="1-3 business days">1-3 business days</SelectItem>
                        <SelectItem value="3-5 business days">3-5 business days</SelectItem>
                        <SelectItem value="1 week">1 week</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Fee Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">BidZuu Seller Fees</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• 10% commission on final sale price</li>
                  <li>• Plus applicable state tax on the fee</li>
                  <li>• Payout sent after sale completion</li>
                </ul>
                {formData.price && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <p className="text-sm text-blue-800">
                      <strong>Example:</strong> If sold at ${parseFloat(formData.price || 0).toFixed(2)}, 
                      you receive ~${(parseFloat(formData.price || 0) * 0.90).toFixed(2)} (before tax on fee)
                    </p>
                  </div>
                )}
              </div>

              {/* Summary */}
              <Card className="p-4 bg-slate-50">
                <h3 className="font-semibold mb-4">Listing Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Title</span>
                    <span className="font-medium">{formData.title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Category</span>
                    <span className="font-medium capitalize">{formData.category}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Type</span>
                    <span className="font-medium capitalize">{formData.listing_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {formData.listing_type === 'auction' ? 'Starting Bid' : 'Price'}
                    </span>
                    <span className="font-medium">${parseFloat(formData.price || 0).toFixed(2)}</span>
                  </div>
                  {formData.listing_type === 'auction' && formData.reserve_price && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Reserve Price</span>
                      <span className="font-medium">${parseFloat(formData.reserve_price).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Photos</span>
                    <span className="font-medium">{formData.images.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Shipping</span>
                    <span className="font-medium">{formData.shipping.free_shipping ? 'Free' : 'Buyer Pays'}</span>
                  </div>
                  {formData.location && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Location</span>
                      <span className="font-medium">
                        {formData.location.city || formData.location.country || 'Set'}
                      </span>
                    </div>
                  )}
                </div>
              </Card>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1 rounded-full" onClick={() => setStep(2)}>
                  Back
                </Button>
                <Button 
                  className="flex-1 rounded-full bg-emerald-500 hover:bg-emerald-600"
                  onClick={handleSubmit}
                  disabled={submitting || !formData.price}
                  data-testid="submit-listing"
                >
                  {submitting ? "Creating..." : "Create Listing"}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default SellPage;
